import { Campaign } from '../types/marketing';
import { Product } from '../redux/slices/productSlice';

// Simulação de API do WhatsApp
const API_URL = 'http://localhost:3001/api/whatsapp';

// Interface para informações de contato do WhatsApp
export interface WhatsAppContact {
  id: string;
  phoneNumber: string;
  name: string;
  profilePicUrl?: string;
  isBusinessContact?: boolean;
  businessName?: string;
  lastInteraction?: Date;
  status?: 'online' | 'offline' | 'typing';
  about?: string;
  lastMessage?: string;
  detectedEmail?: string; // Adicionado campo para armazenar e-mail detectado
}

// Interface para novo lead
export interface NewLead {
  name: string;
  phoneNumber: string;
  email?: string;
  source: string;
  profilePicUrl?: string;
  notes?: string;
  tags?: string[];
}

export const whatsappService = {
  async sendMessage(phoneNumber: string, message: string, options?: {
    attachments?: string[];
    buttons?: Array<{ text: string; id: string }>;
    template?: string;
  }): Promise<void> {
    const response = await fetch(`${API_URL}/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phoneNumber,
        message,
        ...options,
      }),
    });

    if (!response.ok) {
      throw new Error('Erro ao enviar mensagem via WhatsApp');
    }
  },

  async sendCampaign(campaign: Campaign): Promise<void> {
    // Verificar se o tipo existe antes de comparar
    if (campaign.type && campaign.type !== 'whatsapp') {
      throw new Error('Campanha não é do tipo WhatsApp');
    }

    const results = await Promise.allSettled(
      campaign.recipients.map((recipient: string) =>
        this.sendMessage(recipient, campaign.content, {
          template: campaign.template,
          // Adicionar id aos botões se não existirem
          buttons: campaign.buttons?.map((button: { text: string; url?: string; action?: string; id?: string }, index: number) => ({
            ...button,
            id: button.id || `btn_${index}`
          })),
        })
      )
    );

    const failures = results.filter((result: { status: string }) => result.status === 'rejected');
    if (failures.length > 0) {
      console.error(`${failures.length} mensagens falharam ao enviar`);
      throw new Error('Algumas mensagens não puderam ser enviadas');
    }
  },

  async getMessageStatus(messageId: string): Promise<{
    status: 'sent' | 'delivered' | 'read' | 'failed';
    timestamp: string;
    error?: string;
    retryCount?: number;
  }> {
    const response = await fetch(`${API_URL}/messages/${messageId}/status`);

    if (!response.ok) {
      throw new Error('Erro ao buscar status da mensagem');
    }

    return response.json();
  },

  async getQRCode(): Promise<string> {
    const response = await fetch(`${API_URL}/qrcode`);

    if (!response.ok) {
      throw new Error('Erro ao gerar QR Code');
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  async getConnectionStatus(): Promise<{
    connected: boolean;
    lastSeen?: string;
    battery?: number;
    isCharging?: boolean;
    deviceInfo?: {
      platform: string;
      version: string;
    };
  }> {
    const response = await fetch(`${API_URL}/status`);

    if (!response.ok) {
      throw new Error('Erro ao verificar status da conexão');
    }

    return response.json();
  },

  async scheduleMessage(phoneNumber: string, message: string, scheduledTime: Date, options?: {
    attachments?: string[];
    buttons?: Array<{ text: string; id: string }>;
    template?: string;
  }): Promise<string> {
    const response = await fetch(`${API_URL}/schedule`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phoneNumber,
        message,
        scheduledTime: scheduledTime.toISOString(),
        ...options,
      }),
    });

    if (!response.ok) {
      throw new Error('Erro ao agendar mensagem');
    }

    const { scheduleId } = await response.json();
    return scheduleId;
  },

  async cancelScheduledMessage(scheduleId: string): Promise<void> {
    const response = await fetch(`${API_URL}/schedule/${scheduleId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Erro ao cancelar mensagem agendada');
    }
  },

  async getTemplates(): Promise<Array<{
    id: string;
    name: string;
    content: string;
    variables: string[];
  }>> {
    const response = await fetch(`${API_URL}/templates`);

    if (!response.ok) {
      throw new Error('Erro ao buscar templates');
    }

    return response.json();
  },

  // Novos métodos para extrair informações de contato e cadastrar leads

  async getContactInfo(phoneNumber: string): Promise<WhatsAppContact> {
    // Remove caracteres não numéricos para normalizar o número
    const normalizedPhone = phoneNumber.replace(/\D/g, '');
    
    const response = await fetch(`${API_URL}/contacts/${normalizedPhone}`);

    if (!response.ok) {
      throw new Error('Erro ao buscar informações do contato');
    }

    return response.json();
  },

  async getContactProfilePic(phoneNumber: string): Promise<string | null> {
    const normalizedPhone = phoneNumber.replace(/\D/g, '');
    
    try {
      const response = await fetch(`${API_URL}/contacts/${normalizedPhone}/profile-pic`);

      if (!response.ok) {
        return null;
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Erro ao buscar foto de perfil:', error);
      return null;
    }
  },

  async getRecentContacts(limit: number = 20): Promise<WhatsAppContact[]> {
    const response = await fetch(`${API_URL}/contacts/recent?limit=${limit}`);

    if (!response.ok) {
      throw new Error('Erro ao buscar contatos recentes');
    }

    return response.json();
  },

  async createLeadFromWhatsAppContact(phoneNumber: string, source: string = 'WhatsApp'): Promise<NewLead> {
    try {
      // Buscar informações do contato
      const contactInfo = await this.getContactInfo(phoneNumber);
      
      // Buscar foto de perfil
      const profilePicUrl = await this.getContactProfilePic(phoneNumber);
      
      // Criar objeto de lead
      const newLead: NewLead = {
        name: contactInfo.name || contactInfo.businessName || 'Nome não disponível',
        phoneNumber: contactInfo.phoneNumber,
        email: contactInfo.detectedEmail || undefined, // Incluir e-mail se foi detectado
        source,
        profilePicUrl: profilePicUrl || undefined,
        notes: `Lead gerado automaticamente a partir de contato do WhatsApp em ${new Date().toLocaleString()}`,
        tags: ['whatsapp', 'auto-generated']
      };
      
      return newLead;
    } catch (error) {
      console.error('Erro ao criar lead a partir do contato do WhatsApp:', error);
      throw new Error('Não foi possível criar o lead a partir do contato do WhatsApp');
    }
  },

  // Webhook para receber eventos do WhatsApp
  async registerWebhook(callbackUrl: string): Promise<void> {
    const response = await fetch(`${API_URL}/webhook/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        callbackUrl,
      }),
    });

    if (!response.ok) {
      throw new Error('Erro ao registrar webhook');
    }
  },

  // Configurar captura automática de contatos
  async configureAutoLeadCapture(enabled: boolean, options?: {
    autoCreateLead: boolean;
    captureProfilePic: boolean;
    notifyNewContacts: boolean;
  }): Promise<void> {
    const response = await fetch(`${API_URL}/settings/auto-lead-capture`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        enabled,
        ...options,
      }),
    });

    if (!response.ok) {
      throw new Error('Erro ao configurar captura automática de leads');
    }
  },

  // Processar evento de nova mensagem
  async processNewMessageEvent(event: {
    messageId: string;
    from: string;
    to: string;
    text: string;
    timestamp: string;
    isGroup: boolean;
  }): Promise<WhatsAppContact | null> {
    // Ignorar mensagens de grupo
    if (event.isGroup) return null;

    try {
      // Extrair informações do contato
      const contact = await this.getContactInfo(event.from);
      
      // Atualizar última mensagem
      contact.lastMessage = event.text;
      
      // Detectar e-mail na mensagem
      const detectedEmail = this.detectEmailInMessage(event.text);
      if (detectedEmail) {
        // Adicionar e-mail detectado ao contato para uso posterior
        contact.detectedEmail = detectedEmail;
        
        // Atualizar o lead com o e-mail detectado
        this.updateLeadWithDetectedEmail(event.from, detectedEmail)
          .catch(error => console.error('Erro ao atualizar lead com e-mail:', error));
      }
      
      return contact;
    } catch (error) {
      console.error('Erro ao processar evento de nova mensagem:', error);
      return null;
    }
  },

  // Novos métodos para integração com produtos
  
  // Enviar informações de produto via WhatsApp
  async sendProductInfo(phoneNumber: string, product: Product): Promise<void> {
    try {
      const productMessage = `*${product.name}*\n\n${product.description}\n\n*Preço:* R$ ${product.price.toFixed(2)}\n*Categoria:* ${product.category || 'Não categorizado'}\n*Tipo:* ${product.type === 'produto' ? 'Produto' : 'Serviço'}`;
      
      const options: any = {};
      
      // Adicionar imagem se disponível
      if (product.imageUrl) {
        options.attachments = [product.imageUrl];
      }
      
      // Adicionar botões de ação
      options.buttons = [
        { text: "Mais informações", id: `product_info_${product.id}` },
        { text: "Tenho interesse", id: `product_interest_${product.id}` }
      ];
      
      await this.sendMessage(phoneNumber, productMessage, options);
      
      return;
    } catch (error) {
      console.error('Erro ao enviar informações do produto:', error);
      throw new Error('Falha ao enviar informações do produto via WhatsApp');
    }
  },
  
  // Enviar catálogo de produtos
  async sendProductCatalog(phoneNumber: string, products: Product[], category?: string): Promise<void> {
    try {
      // Filtrar por categoria se especificada
      const filteredProducts = category 
        ? products.filter(p => p.category === category)
        : products;
      
      if (filteredProducts.length === 0) {
        await this.sendMessage(phoneNumber, `Não encontramos produtos${category ? ` na categoria ${category}` : ''}.`);
        return;
      }
      
      // Enviar mensagem introdutória
      await this.sendMessage(
        phoneNumber, 
        `Aqui está ${category ? `nossa seleção de ${category}` : 'nosso catálogo de produtos e serviços'}:`
      );
      
      // Enviar até 5 produtos para não sobrecarregar
      const productsToSend = filteredProducts.slice(0, 5);
      
      // Enviar cada produto individualmente
      for (const product of productsToSend) {
        await this.sendProductInfo(phoneNumber, product);
        // Pequeno delay para não sobrecarregar a API
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Se houver mais produtos, informar
      if (filteredProducts.length > 5) {
        await this.sendMessage(
          phoneNumber, 
          `Mostrando 5 de ${filteredProducts.length} itens disponíveis. Escreva "mais produtos" para ver mais opções.`
        );
      }
      
      return;
    } catch (error) {
      console.error('Erro ao enviar catálogo de produtos:', error);
      throw new Error('Falha ao enviar catálogo de produtos via WhatsApp');
    }
  },
  
  // Processar resposta a botões de produtos
  async processProductButtonResponse(
    buttonId: string, 
    phoneNumber: string, 
    productId: string
  ): Promise<void> {
    try {
      if (buttonId.startsWith('product_info_')) {
        // Usuário solicitou mais informações
        await this.sendMessage(
          phoneNumber, 
          `Obrigado pelo seu interesse! Um de nossos consultores entrará em contato em breve com mais informações sobre este item.`
        );
      } else if (buttonId.startsWith('product_interest_')) {
        // Usuário demonstrou interesse em comprar
        await this.sendMessage(
          phoneNumber, 
          `Ótimo! Registramos seu interesse neste item. Um de nossos consultores entrará em contato em breve para finalizar seu pedido.`
        );
      }
      
      return;
    } catch (error) {
      console.error('Erro ao processar resposta de botão de produto:', error);
      throw new Error('Falha ao processar resposta de botão de produto');
    }
  },
  
  // Detectar intenções relacionadas a produtos nas mensagens
  async detectProductIntent(message: string): Promise<{
    hasProductIntent: boolean;
    category?: string;
    productId?: string;
    action?: 'list' | 'info' | 'price' | 'availability';
  }> {
    // Palavras-chave para detectar intenções relacionadas a produtos
    const productKeywords = ['produto', 'produtos', 'serviço', 'serviços', 'catálogo', 'preço', 'valor', 'custo', 'comprar', 'adquirir'];
    
    // Verificar se a mensagem contém palavras-chave de produtos
    const hasProductKeyword = productKeywords.some(keyword => 
      message.toLowerCase().includes(keyword)
    );
    
    if (!hasProductKeyword) {
      return { hasProductIntent: false };
    }
    
    // Análise básica de intenção
    const result: {
      hasProductIntent: boolean;
      category?: string;
      productId?: string;
      action?: 'list' | 'info' | 'price' | 'availability';
    } = {
      hasProductIntent: true
    };
    
    // Detectar ação
    if (message.match(/catalogo|lista|mostrar|ver|quais/i)) {
      result.action = 'list';
    } else if (message.match(/informações|detalhes|especificações|como é/i)) {
      result.action = 'info';
    } else if (message.match(/preço|valor|custo|quanto custa/i)) {
      result.action = 'price';
    } else if (message.match(/disponível|disponibilidade|tem em estoque|quando chega/i)) {
      result.action = 'availability';
    }
    
    // Tentar extrair categorias comuns
    const categoryMatches = message.match(/imoveis|casas|apartamentos|terrenos|aluguel|venda|compra|financiamento/i);
    if (categoryMatches) {
      result.category = categoryMatches[0].toLowerCase();
    }
    
    return result;
  },
  
  // Função para detectar e-mail em uma mensagem
  detectEmailInMessage(message: string): string | null {
    // Regex para detectar padrões de e-mail
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    
    // Encontrar todos os possíveis e-mails na mensagem
    const matches = message.match(emailRegex);
    
    // Retornar o primeiro e-mail encontrado ou null se não houver
    return matches ? matches[0] : null;
  },
  
  // Atualizar lead com e-mail detectado na conversa
  async updateLeadWithDetectedEmail(phoneNumber: string, email: string): Promise<void> {
    try {
      // Importar o serviço de leads dinamicamente para evitar dependência circular
      const { leadService } = await import('./leadService');
      
      // Buscar informações do contato
      const contactInfo = await this.getContactInfo(phoneNumber);
      
      // Criar um objeto de lead com o e-mail detectado
      const leadData: NewLead = {
        name: contactInfo.name || contactInfo.businessName || 'Nome não disponível',
        phoneNumber: contactInfo.phoneNumber,
        email: email,
        source: 'WhatsApp',
        notes: `E-mail detectado na conversa do WhatsApp: ${email} (${new Date().toLocaleString()})`,
        tags: ['whatsapp', 'email-detected']
      };
      
      // Atualizar ou criar o lead
      await leadService.createLeadFromWhatsApp(leadData);
      
      console.log(`Lead atualizado com e-mail detectado: ${email} para o contato ${phoneNumber}`);
    } catch (error) {
      console.error('Erro ao atualizar lead com e-mail detectado:', error);
    }
  },
};